import React, { useState, useMemo } from 'react';
import {
  FormControl,
  Autocomplete,
  TextField,
  CircularProgress,
  Button,
  Tooltip,
  Box,
  Typography,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { Company } from '../../models/Company';
import { useSearchCompaniesAutocomplete } from '../../hooks/useCompany';
import { zoullooColors } from '../../theme';

interface CompanyAutocompleteProps {
  value?: Company | null;
  required?: boolean;
  disabled?: boolean;
  error?: string;
  label?: string;
  placeholder?: string;
  showCreateButton?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  onChange: (company: Company | null) => void;
  onCreateNew?: () => void;
}

const CompanyAutocomplete: React.FC<CompanyAutocompleteProps> = ({
  value,
  required = false,
  disabled = false,
  error,
  label,
  placeholder,
  showCreateButton = false,
  helperText,
  size = 'medium',
  fullWidth = true,
  onChange,
  onCreateNew,
}) => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');

  // Search companies when user types
  const { data: searchedCompanies = [], isLoading: isSearching } = useSearchCompaniesAutocomplete(
    searchTerm,
    2 // minimum 2 characters
  );

  // Only show options when user has typed at least 2 characters
  const options = useMemo(() => {
    if (searchTerm.length < 2) {
      return [];
    }

    // Sort alphabetically by legal name
    return searchedCompanies
      .filter((company, index, self) => 
        index === self.findIndex(c => c.id === company.id) // Remove duplicates
      )
      .sort((a, b) => 
        (a.legalName || '').localeCompare(b.legalName || '', undefined, { 
          sensitivity: 'base',
          numeric: true 
        })
      );
  }, [searchedCompanies, searchTerm]);

  const handleInputChange = (_: React.SyntheticEvent, newInputValue: string) => {
    setSearchTerm(newInputValue);
  };

  const handleChange = (_: unknown, newValue: Company | null) => {
    onChange(newValue);
  };

  const getNoOptionsText = () => {
    if (searchTerm.length < 2) {
      return t('company.autocomplete.typeMoreChars');
    }
    return t('company.noCompaniesFound');
  };

  const getPlaceholderText = () => {
    if (placeholder) return placeholder;
    return t('company.autocomplete.searchPlaceholder');
  };

  const getLabelText = () => {
    if (label !== undefined) return label;
    return t('project.company');
  };

  return (
    <FormControl fullWidth={fullWidth} required={required} error={!!error}>
      <Tooltip
        title={disabled ? t('company.selectionDisabled') : ''}
        placement="top"
      >
        <Box>
          <Autocomplete
            value={value || null}
            onChange={handleChange}
            onInputChange={handleInputChange}
            options={options}
            getOptionLabel={(option) => option.tradingName || option.legalName || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            loading={isSearching && searchTerm.length >= 2}
            loadingText={t('common.loading')}
            noOptionsText={getNoOptionsText()}
            disabled={disabled}
            size={size}
            slotProps={{
              paper: {
                elevation: 8,
                sx: {
                  zIndex: 1300,
                  backgroundColor: 'background.paper',
                }
              },
              popper: {
                sx: {
                  zIndex: 1300,
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={getLabelText()}
                placeholder={getPlaceholderText()}
                required={required}
                error={!!error}
                helperText={error || helperText}
                sx={{
                  '& .MuiInputBase-root': {
                    height: '56px',
                    minHeight: '56px',
                  },
                  '& .MuiInputBase-input': {
                    height: 'auto',
                    padding: '16.5px 14px',
                  },
                  '& .MuiInputLabel-root': {
                    top: '0px',
                  },
                  '& .MuiInputLabel-shrink': {
                    top: '-6px',
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <>
                      <SearchIcon sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
                      {params.InputProps.startAdornment}
                    </>
                  ),
                  endAdornment: (
                    <>
                      {isSearching && searchTerm.length >= 2 ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <BusinessIcon sx={{ color: 'action.active', mr: 1 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body1">
                      {option.tradingName || option.legalName}
                    </Typography>
                    {option.tradingName && option.tradingName !== option.legalName && (
                      <Typography variant="caption" color="text.secondary">
                        {option.legalName}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            )}
            PaperComponent={({ children, ...other }) => (
              <Box {...other}>
                {children}
              </Box>
            )}
          />
          
         
        </Box>
      </Tooltip>
      
      {showCreateButton && !disabled && onCreateNew && (
        <Button
          onClick={onCreateNew}
          sx={{ 
            mt: 1, 
            alignSelf: 'flex-start',
            color: zoullooColors.secondary.main,
            '&:hover': {
              backgroundColor: zoullooColors.secondary.alpha
            }
          }}
          startIcon={<BusinessIcon />}
          size="small"
        >
          {t('company.createNew')}
        </Button>
      )}
    </FormControl>
  );
};

export default CompanyAutocomplete;